import React, {useContext, useState} from "react";
import {useDispatch} from "react-redux";
import {useTheme} from "connectui/theme/ThemeProvider";
import ICONS from "connectui/icons/iconMap";
import {InboxContext} from "routes/MessagesPage";
import {updateGuest} from "redux/actions/guestsActions";
import {createAction} from "redux/actions/experiencesActions";
import {
  closeGeneralSuccessAlert,
  openGeneralSuccessAlert,
} from "redux/actions/settingsActions";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";

import FlexContainer from "connectui/component/layout/FlexContainer";
import SvgIcon from "connectui/component/display/SvgIcon";
import ButtonIconText from "connectui/component/input/button/ButtonIconText";
import GuestTypeSelector from "./GuestTypeSelector";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import {buildStyles} from "connectui/component/buildStyles";
import ContextMenu from "connectui/component/input/context/ContextMenu";
import SnoozeButton from "./SnoozeButton";
import Skeleton from "connectui/component/display/Skeleton";
import ContextMenuItem from "connectui/component/input/context/ContextMenuItem";
import Container from "../../../../../connectui/component/layout/Container";

const useStyles = (theme) =>
  buildStyles({
    iconBtn: {
      borderRadius: "50%",
      backgroundColor: theme.colors.white,
      padding: "0px",
      border: "none",
      outline: "none",
      "&:hover": {backgroundColor: theme.colors.hoverGray},
    },
    icon: {
      width: "28px",
      height: "28px",
      justifyContent: "center",
      padding: theme.spacing(1),
      color: theme.colors.textSecondary,
    },
  });

const HeaderActions = ({guest, loading, headerSize}) => {
  const {theme, isMobile} = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    setThreads,
    appliedFilters,
    stageCounts,
    setStageCounts,
    setGuestUpdateInProgress,
  } = useContext(InboxContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snoozeMenuOpen, setSnoozeMenuOpen] = useState(false);
  const hideActions = headerSize.width < 610;
  const compactSelector = headerSize.width < 400 || isMobile;
  const {t} = useTranslation();

  const updateStageCounts = (oldStage, newStage) => {
    if (!stageCounts || !oldStage || !newStage) return;

    setStageCounts((prev) => ({
      ...prev,
      [oldStage]: Math.max(0, (prev[oldStage] || 0) - 1),
      [newStage]: (prev[newStage] || 0) + 1,
    }));
  };

  const handleSnooze = (timestamp) => {
    setThreads((prev) => prev.filter((g) => g.guest_id !== guest.guest_id));
    updateStageCounts(guest.conversation_stage, "followup");
    history.push("/admin/messages");
    setGuestUpdateInProgress(true);

    let action = {
      action_id: "update_guest",
      id: "followup",
      body: {
        conversation_stage: "todo",
      },
    };

    let trigger = {
      trigger: "specific_time",
      trigger_value: timestamp,
    };

    dispatch(
      createAction({
        trigger: !!Object.keys(trigger).length ? trigger : null,
        guestId: guest.guest_id,
        action,
        onSuccess: (response) => {
          dispatch(
            updateGuest({
              guest_id: guest.guest_id,
              field: "conversation_stage",
              val: "followup",
              onSuccess: () => {
                setTimeout(() => {
                  setGuestUpdateInProgress(false);
                }, 2000);
              },
              onError: () => {
                setGuestUpdateInProgress(false);
              },
            }),
          );
        },
        onError: () => {
          setGuestUpdateInProgress(false);
        },
      }),
    );
  };

  const handleResolve = () => {
    const newStage =
      guest.conversation_stage === "resolved" ? "todo" : "resolved";
    setThreads((prev) => prev.filter((g) => g.guest_id !== guest.guest_id));
    updateStageCounts(guest.conversation_stage, newStage);
    history.push("/admin/messages");
    setGuestUpdateInProgress(true);

    dispatch(
      updateGuest({
        guest_id: guest.guest_id,
        field: "conversation_stage",
        val: newStage,
        disableAlert: true,
        onSuccess: () => {
          dispatch(
            openGeneralSuccessAlert({
              message: t(
                "inbox.conversations.header.actions.success.stage-update",
              ),
              onClose: () => dispatch(closeGeneralSuccessAlert()),
            }),
          );
          setTimeout(() => {
            setGuestUpdateInProgress(false);
          }, 2000);
        },
        onError: () => {
          setGuestUpdateInProgress(false);
        },
      }),
    );
  };

  const removeGuestFromList = () => {
    setThreads((prev) => prev.filter((g) => g.guest_id !== guest.guest_id));
  };

  const toggleArchive = () => {
    let newTags = [...(guest.tags ?? [])];
    const isArchiveFilterActive = appliedFilters.find(
      (f) => f.path === "guest_tags" && f.value === "archived",
    );

    if (newTags.includes("archived")) {
      newTags = newTags.filter((f) => f !== "archived");
      if (isArchiveFilterActive) {
        removeGuestFromList();
        history.push("/admin/messages");
      }
    } else {
      newTags.push("archived");
      if (!isArchiveFilterActive) {
        removeGuestFromList();
        history.push("/admin/messages");
      }
    }

    setGuestUpdateInProgress(true);
    dispatch(
      updateGuest({
        guest_id: guest.guest_id,
        field: "tags",
        val: newTags,
        onSuccess: () => {
          setTimeout(() => {
            setGuestUpdateInProgress(false);
          }, 2000);
        },
        onError: () => {
          setGuestUpdateInProgress(false);
        },
      }),
    );
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSnoozeMenuOpen(false);
  };

  if (!guest.guest_id) return null;
  return (
    <FlexContainer
      justifyContent="flex-end"
      alignItems="center"
      gap={compactSelector ? 0 : theme.spacing(1)}
      sx={{flex: 1, marginLeft: theme.spacing(1)}}
    >
      {loading ? (
        <Skeleton
          width={"90px"}
          height={"28px"}
          style={{
            borderRadius: theme.spacing(4),
            marginRight: theme.spacing(1),
          }}
        />
      ) : (
        <Container display="flex">
          <GuestTypeSelector
            compact={compactSelector}
            selected={guest.guest_type ?? "guest"}
            onChange={(type) =>
              dispatch(
                updateGuest({
                  guest_id: guest.guest_id,
                  field: "guest_type",
                  val: type,
                }),
              )
            }
          />
        </Container>
      )}
      {!hideActions ? (
        loading ? (
          <FlexContainer alignItems="center" gap={theme.spacing(1)}>
            {Array.from({length: 2}).map((_, i) => (
              <Skeleton
                key={i}
                width={"90px"}
                height={"28px"}
                style={{borderRadius: theme.spacing(4)}}
              />
            ))}
          </FlexContainer>
        ) : (
          <>
            <SnoozeButton handleSnooze={handleSnooze} />
            <ButtonIconText
              label={
                guest.conversation_stage === "resolved"
                  ? "Unresolve"
                  : "Resolve"
              }
              weight="medium"
              color="primary25"
              typographyVariant="textSM"
              sx={{
                backgroundColor: theme.colors.primary,
                borderRadius: theme.spacing(4),
                padding: theme.spacing(1, 3),
                "&:hover": {backgroundColor: theme.colors.primary600},
              }}
              onClick={handleResolve}
              icon={
                <SvgIcon
                  component={ICONS["check-circle"]}
                  sx={{
                    marginRight: theme.spacing(1),
                    width: "14px",
                    height: "14px",
                    color: theme.colors.primary100,
                  }}
                />
              }
            />
          </>
        )
      ) : loading ? (
        <Skeleton width="28px" height="28px" style={{borderRadius: "50%"}} />
      ) : (
        <>
          <SnoozeButton
            menuOptions={{
              menuEnabled: true,
              menuAnchorEl: snoozeMenuOpen && anchorEl,
              closeMenu: (keepMenuOpen) => {
                if (keepMenuOpen) {
                  setSnoozeMenuOpen(false);
                } else {
                  closeMenu();
                }
              },
            }}
            handleSnooze={handleSnooze}
          />
          <ContextMenu
            menuItems={[
              {
                Component: ({label}) => (
                  <ContextMenuItem
                    label={label}
                    icon={ICONS["alarm-snooze"]}
                    actions={
                      <SvgIcon
                        sx={{
                          width: "10px",
                          height: "10px",
                          color: theme.colors.textTertiary,
                          margin: theme.spacing(0.5, -1, 0, 2),
                        }}
                        component={ICONS["chevron-right"]}
                      />
                    }
                    onClick={() => setSnoozeMenuOpen(true)}
                  />
                ),
                label: t("inbox.conversations.header.actions.snooze"),
              },
              {
                label: t("inbox.conversations.header.actions.resolve"),
                icon: ICONS["check-circle"],
                onClick: () => {
                  closeMenu();
                  handleResolve();
                },
              },
              {
                label: guest?.tags?.includes("archived")
                  ? t("inbox.conversations.header.actions.unarchive")
                  : t("inbox.conversations.header.actions.archive"),
                icon: ICONS.archive,
                onClick: () => {
                  closeMenu();
                  toggleArchive();
                },
              },
            ]}
            open={!!anchorEl && !snoozeMenuOpen}
            anchorEl={anchorEl}
            modalProps={{
              modalPosition: {horizontal: "end"},
            }}
            onClose={() => closeMenu()}
          />
          <ButtonIcon
            icon={
              <SvgIcon
                component={ICONS["more-vertical"]}
                className={classes.icon}
              />
            }
            className={classes.iconBtn}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          />
        </>
      )}
    </FlexContainer>
  );
};

export default HeaderActions;
